import { useState } from "react";
import { cn } from "@/lib/utils";
import { toast } from "@/lib/toast";
import { parseFilenameFromHeader, triggerBlobDownload } from "@/lib/download";

// PDF/Excel are regenerated server-side on request, which takes a few seconds.
// Fetch the blob ourselves (instead of a plain <a download>) so we can show a
// "Preparing…" state while it generates, then trigger the download.
export function DownloadButton({
  url,
  filename,
  children,
}: {
  url: string;
  filename?: string;
  children: React.ReactNode;
}) {
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(url, { credentials: "same-origin" });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const blob = await res.blob();
      const name = filename || parseFilenameFromHeader(res.headers.get("content-disposition")) || "download";
      triggerBlobDownload(blob, name);
    } catch (err) {
      toast.error(`Download failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      aria-busy={busy}
      className={cn(
        "inline-flex h-8 items-center justify-center gap-1.5 rounded-md px-3",
        "border border-rule bg-surface text-[0.82rem] font-medium text-ink",
        "transition-all hover:border-rule-strong hover:bg-surface-2",
        "focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2",
        "disabled:cursor-wait disabled:opacity-70",
      )}
    >
      {busy && (
        <svg className="h-3.5 w-3.5 animate-spin text-ink-muted" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {busy ? "Preparing…" : children}
    </button>
  );
}
